import {
  Suspense,
  useEffect,
  useState,
  useMemo,
  useRef,
  lazy,
} from "react";
import { Outlet, useLocation } from "react-router-dom";
import { isExpired } from "react-jwt";
import { LANGUAGEMAP, RIMS_LOCAL_STG_CONSTANTS } from "../constants";
import { hasMenuAuth } from "../common/utils";
import { Stack } from "@mui/material";
import { createTheme, ThemeProvider } from "@mui/material/styles";
import type {} from "@mui/x-data-grid-premium/themeAugmentation";
import { gridPaginationThemeDefaults } from "@freyr/freyr-react-common/grid-layout";
import Loading from "../components/Loading";
import { muiLocaleBundles, type MuiLocaleBundleKey } from "./muiLocales";
import { useConfigStore } from "../store/config";
import {
  apiGetTenant,
  getSpecifyRelatedResourceList,
} from "../api/systemLibrary";
import { useCookieData } from "../hooks/useCookieData";
import useSystemStore from "../store/system";
import classnames from "classnames";
import styles from "./index.module.scss";
import { useQuery } from "@tanstack/react-query";
import useGlobalFeatureFlag from "../store/globalFeatureFlag";
import { useSnackbar } from "notistack";
import { useTranslation } from "react-i18next";
import useGlobalLoading from "../store/globalLoading";
import { parseTenantOnboardingResponseToRtqSettings } from "../common/parseTenantOnboarding";
import useSideMenuState from "../store/sideMenuStore";
import { useAppIsFetching } from "../hooks/useAppIsFetching";
import UnifiedFreyrComponentProviders from "../providers/UnifiedFreyrComponentProviders";
import { FreyrLibraryProvider } from "@freyr/freyr-react-common";

const ESignValidationWapper = lazy(
  () => import("../components/ESignValidationWapper"),
);

const checkCurrentPathAuth = (authList: API.AuthItem[], curPath: string) => {
  const rules = [
    { path: "/rtqs/edit-rtq", auth: "rtq_edit" },
    { path: "/rtqs/view-rtq", auth: "rtq_view" },
    { path: "/rtqs/add-rtq", auth: "rtq_add" },
  ];
  if (
    (curPath === "/rtqs" || curPath === "/rtqs/") &&
    hasMenuAuth(authList, "rtq_view")
  ) {
    return true;
  }
  return rules.some(
    (rule) =>
      curPath.includes(rule.path) && hasMenuAuth(authList, rule.auth),
  );
};

const Layouts = () => {
  // const [loading, setLoading] = useState(false);
  const [auth, setAuth] = useState<boolean>(false);
  const [storeLanguage] = useConfigStore((state) => [state.language]);
  const language = storeLanguage || "en";
  // const URLDomain = getURLDomain();
  // const redirectURL = `https://portal.${URLDomain}`;
  const { pathname } = useLocation();
  const location = useLocation();
  const [updateSystem] = useSystemStore((state: any) => [state.updateSystem]);
  const { token, userId, tenantId, domain, userEmail, userInfo } =
    useCookieData();

  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const themeWithLocale = useMemo(() => {
    const key = LANGUAGEMAP.mui[language] as MuiLocaleBundleKey;
    const { core, grid, pickers } = muiLocaleBundles[key];
    return createTheme(
      {
        palette: {},
        components: {
          MuiDataGrid: gridPaginationThemeDefaults,
          MuiFormControl: {
            styleOverrides: {
              root: {
                ".MuiDataGrid-toolbarContainer &": {
                  width: "160px",
                  flex: "1 1 auto",
                  maxWidth: "50%",
                },
              },
            },
          },
        },
      },
      core,
      grid,
      pickers,
    );
  }, [language]);

  const sessionReady = useMemo(() => {
    if (token === null || !userId) return false;
    try {
      return !isExpired(token);
    } catch {
      return false;
    }
  }, [token, userId]);
  
  const {
    data: menuAuthList,
    isSuccess: menuAuthSuccess,
    isError: menuAuthIsError,
    isFetched: menuAuthFetched,
  } = useQuery({
    queryKey: ["menuAuthList", userId],
    queryFn: async () => {
      const res = await getSpecifyRelatedResourceList({
        userId: userId!,
        resourceType: "menu",
        app: "",
        module: "8",
        subModule: "",
        parentId: null,
      });
      const raw = (res as any).data ?? [];
      const list = Array.isArray(raw) ? raw : [];
      return list.map((o: any) => ({
        name: o.name,
        parentName: o.parentName,
        displayName: o.displayName,
      })) as API.AuthItem[];
    },
    enabled: sessionReady && !!userId,
    refetchOnWindowFocus: false,
    retry: false,
  });

  const menuAuthAppliedRef = useRef(false);

  useEffect(() => {
    menuAuthAppliedRef.current = false;
  }, [userId, sessionReady]);

  useEffect(() => {
    if (!userId) {
      setAuth(false);
    }
  }, [userId]);

  useEffect(() => {
    if (!sessionReady || !menuAuthFetched || !menuAuthIsError) {
      return;
    }
    setAuth(false);
    window.localStorage.setItem(
      RIMS_LOCAL_STG_CONSTANTS.MENU_AUTH_LIST,
      JSON.stringify([]),
    );
  }, [sessionReady, menuAuthFetched, menuAuthIsError]);

  useEffect(() => {
    if (!sessionReady || !menuAuthSuccess || menuAuthList === undefined) {
      return;
    }
    if (menuAuthAppliedRef.current) {
      return;
    }
    menuAuthAppliedRef.current = true;

    const curPath = location.pathname;
    if (
      curPath.includes("global-search-list") ||
      checkCurrentPathAuth(menuAuthList, curPath)
    ) {
      window.localStorage.setItem(
        RIMS_LOCAL_STG_CONSTANTS.MENU_AUTH_LIST,
        JSON.stringify(menuAuthList),
      );
      setAuth(true);
    } else {
      window.localStorage.setItem(
        RIMS_LOCAL_STG_CONSTANTS.MENU_AUTH_LIST,
        JSON.stringify([]),
      );
      setAuth(false);
      enqueueSnackbar(t("common.tip.noPrivileges"), {
        variant: "error",
        anchorOrigin: { vertical: "top", horizontal: "center" },
      });
    }
    // Path is read when menu auth first succeeds (same timing as the old promise `.then`).
    // eslint-disable-next-line react-hooks/exhaustive-deps -- location.pathname intentionally omitted (see comment above)
  }, [
    sessionReady,
    menuAuthSuccess,
    menuAuthList,
    enqueueSnackbar,
    t,
  ]);

  const { setGlobalFeatureFlags } = useGlobalFeatureFlag();

  const { data: tenantOnboardingConfig } = useQuery({
    queryKey: ["tenantOnboardedModules", tenantId],
    queryFn: () => apiGetTenant(tenantId!),
    enabled: !!tenantId && auth,
    refetchOnWindowFocus: false,
    retry: false,
    select: (res) => parseTenantOnboardingResponseToRtqSettings(res),
  });

  useEffect(() => {
    if (tenantOnboardingConfig?.systemPatch) {
      updateSystem(tenantOnboardingConfig.systemPatch);
    }
  }, [tenantOnboardingConfig?.systemPatch, updateSystem]);

  const globalFeatureFlagPatch = useMemo(
    () => tenantOnboardingConfig?.globalFeatureFlagPatch ?? {},
    [tenantOnboardingConfig],
  );

  const lastAppliedGlobalFeatureFlagsJson = useRef<string | null>(null);

  useEffect(() => {
    lastAppliedGlobalFeatureFlagsJson.current = null;
  }, [tenantId]);

  useEffect(() => {
    if (Object.keys(globalFeatureFlagPatch).length === 0) {
      return;
    }
    const nextJson = JSON.stringify(globalFeatureFlagPatch);
    if (nextJson === lastAppliedGlobalFeatureFlagsJson.current) {
      return;
    }
    lastAppliedGlobalFeatureFlagsJson.current = nextJson;
    setGlobalFeatureFlags(globalFeatureFlagPatch);
  }, [globalFeatureFlagPatch, setGlobalFeatureFlags]);

  const noPaddingPage = useMemo(() => {
    const processPagePaths = [
      "associate-process",
      "initiate-process",
      "global-search-list",
    ];
    return processPagePaths.some((item) => pathname.includes(item));
  }, [pathname]);
  
  const isFetching = useAppIsFetching();
  const freyrUserInfoB64 = btoa(
    encodeURIComponent(JSON.stringify(userInfo)),
  );
  const { globalLoading } = useGlobalLoading();
  const {showMenu} = useSideMenuState();
  return (
    <>
      {!!auth && storeLanguage && (
        <ThemeProvider theme={themeWithLocale}>
          {/* Main list / page grid filters (common grid-filter) read the KM prefix from this context. */}
          <FreyrLibraryProvider
            token={token}
            language={language}
            tenant={tenantId}
            domain="1"
            domainName="MPR"
            envVars={{
              IM_PREFIX: process.env.REACT_APP_API_GETWAY,
            }}
            kmModulePrefix="/hai/km"
          >
          <UnifiedFreyrComponentProviders
            token={token}
            language={language}
            tenant={tenantId}
            domain={"1"}
            domainName={domain}
            useEmail={userEmail}
            envVars={{
              IM_PREFIX: process.env.REACT_APP_API_GETWAY,
              URF_PREFIX: process.env.REACT_APP_API_GETWAY
            }}
            moduleName="RTQ"
            userInfo={freyrUserInfoB64}
          >
            <Suspense fallback={null}>
              <ESignValidationWapper />
            </Suspense>
            <div className={styles["layout"]}>
              <Stack flexDirection="row" sx={{ flex: 1, overflowY: "auto" }}>
                <section className="layout-right">
                  <div
                    className="page-wrapper"
                    style={{ position: "relative" }}
                  >
                    <div
                      className={classnames("customer-page", {
                        [styles["full-custome-page"]]: pathname === "/rtqs",
                        "no-padding-page": noPaddingPage,
                        "no-side-menu":
                          !showMenu && location.pathname !== "/rtqs",
                      })}
                    >
                      <Suspense fallback={<Loading open />}>
                        <Outlet />
                      </Suspense>
                    </div>
                    {/* {isShowSideMenu && !noPaddingPage ? (
                          <>
                            <SideMenu />
                          </>
                        ) : null} */}
                  </div>
                </section>
              </Stack>
            </div>
            <Loading open={isFetching > 0 || globalLoading} />
          </UnifiedFreyrComponentProviders>
          </FreyrLibraryProvider>
        </ThemeProvider>
      )}
    </>
  );
};

export default Layouts;
